import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  Modal,
  Select,
  SimpleTable,
  StatusBadge,
  Textarea,
  textColumn,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import {
  useBillingPreview,
  useBillingRuns,
  useCreateBillingRun,
  useDeleteBillingRun,
  useSubmitRunToSap,
  useUpdateBillingRunStatus,
} from '../api/billingApi.ts'
import type { BillingPreview, BillingRun, BillingRunStatus, RunBillingInput } from '../types.ts'
import { BillingRunDetailDrawer } from '../components/BillingRunDetailDrawer.tsx'

function fmtCurrency(cents: number, currency = 'EUR'): string {
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency })
}

function fmtMonth(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('de-DE', { year: 'numeric', month: 'long' })
}

export function BillingSection() {
  const { toast } = useToast()
  const { data: runs = [], isLoading, error, refetch } = useBillingRuns()
  const updateStatus = useUpdateBillingRunStatus()
  const del = useDeleteBillingRun()
  const submit = useSubmitRunToSap()
  const [createOpen, setCreateOpen] = useState(false)
  const [openRunId, setOpenRunId] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<BillingRun | null>(null)

  function submitToSap(run: BillingRun) {
    submit.mutate(run.id, {
      onSuccess: (r) => {
        if (r.itemsErrored === 0) {
          toast({
            title: 'An SAP übergeben',
            description: `${r.contractsSubmitted} Rechnungs-Drafts angelegt (${r.itemsSubmitted} Posten).`,
            tone: 'success',
          })
        } else {
          toast({
            title: 'Teilweise übergeben',
            description: `${r.itemsSubmitted} ok, ${r.itemsErrored} Fehler — Detailansicht öffnen für Retry.`,
            tone: 'warning',
          })
        }
      },
      onError: (err) =>
        toast({
          title: 'SAP-Übergabe fehlgeschlagen',
          description: err instanceof Error ? err.message : 'Unbekannter Fehler',
          tone: 'danger',
        }),
    })
  }

  function patchStatus(run: BillingRun, status: BillingRunStatus) {
    updateStatus.mutate(
      { id: run.id, status },
      {
        onSuccess: () => toast({ title: 'Status aktualisiert', tone: 'success' }),
        onError: (err) =>
          toast({
            title: 'Statuswechsel fehlgeschlagen',
            description: err instanceof Error ? err.message : 'Unbekannter Fehler',
            tone: 'danger',
          }),
      },
    )
  }

  const columns: ColumnDef<BillingRun, unknown>[] = [
    {
      accessorKey: 'periodStart',
      header: 'Periode',
      size: 160,
      cell: ({ getValue }) => <span className="text-small font-medium text-gray-900">{fmtMonth(getValue() as string)}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 160,
      cell: ({ getValue }) => <StatusBadge status={`billing_${getValue() as string}`} />,
    },
    {
      accessorKey: 'contractsBilled',
      header: 'Verträge',
      size: 100,
      cell: ({ getValue }) => <span className="tabular-nums">{String(getValue())}</span>,
    },
    {
      accessorKey: 'itemsBilled',
      header: 'Posten',
      size: 100,
      cell: ({ getValue }) => <span className="tabular-nums">{String(getValue())}</span>,
    },
    {
      accessorKey: 'totalAmountCents',
      header: 'Summe',
      size: 140,
      cell: ({ row }) => (
        <span className="tabular-nums font-medium text-gray-900">
          {fmtCurrency(row.original.totalAmountCents, row.original.currency)}
        </span>
      ),
    },
    textColumn<BillingRun>('startedAt', 'Erzeugt', { size: 140 }),
    {
      id: '__actions__',
      header: '',
      enableSorting: false,
      size: 280,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          {row.original.status === 'draft' ? (
            <>
              <Button variant="primary" size="xs" onClick={(e) => { e.stopPropagation(); patchStatus(row.original, 'approved') }}>
                Freigeben
              </Button>
              <IconButton
                icon={<Trash2 className="w-3.5 h-3.5" />}
                aria-label="Lauf verwerfen"
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(row.original) }}
              />
            </>
          ) : null}
          {row.original.status === 'approved' ? (
            <>
              <Button
                variant="primary"
                size="xs"
                loading={submit.isPending}
                onClick={(e) => { e.stopPropagation(); submitToSap(row.original) }}
              >
                An SAP übergeben
              </Button>
              <Button variant="ghost" size="xs" onClick={(e) => { e.stopPropagation(); patchStatus(row.original, 'draft') }}>
                Zurück
              </Button>
            </>
          ) : null}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-small text-gray-600">
          Stichtags-Läufe pro Periode. Drafts können freigegeben oder verworfen werden; SAP-Übergabe folgt mit Etappe 6.
        </p>
        <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
          Neuer Abrechnungslauf
        </Button>
      </div>

      <DataTable<BillingRun>
        data={runs}
        columns={columns}
        getRowId={(r) => r.id}
        loading={isLoading}
        error={error ? 'Abrechnungsläufe konnten nicht geladen werden.' : null}
        onRetry={() => void refetch()}
        searchPlaceholder="Läufe suchen …"
        onRowClick={(r) => setOpenRunId(r.id)}
        emptyState={
          <EmptyState
            title="Noch keine Abrechnungsläufe"
            description="Erzeuge eine Vorschau für einen Monat. Der Lauf bleibt als Entwurf, bis du ihn freigibst."
            action={
              <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
                Neuer Abrechnungslauf
              </Button>
            }
          />
        }
      />

      <CreateBillingRunModal open={createOpen} onOpenChange={setCreateOpen} onOpenRun={setOpenRunId} />
      <BillingRunDetailDrawer runId={openRunId} onClose={() => setOpenRunId(null)} />
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Abrechnungslauf verwerfen?"
        message={
          confirmDelete
            ? `Der Draft-Lauf für ${fmtMonth(confirmDelete.periodStart)} (${confirmDelete.itemsBilled} Posten, ${fmtCurrency(
                confirmDelete.totalAmountCents,
                confirmDelete.currency,
              )}) wird gelöscht.`
            : ''
        }
        variant="danger"
        confirmLabel="Verwerfen"
        loading={del.isPending}
        onConfirm={() => {
          if (!confirmDelete) return
          del.mutate(confirmDelete.id, {
            onSuccess: () => {
              toast({ title: 'Lauf verworfen', tone: 'success' })
              setConfirmDelete(null)
            },
            onError: (err) =>
              toast({
                title: 'Löschen fehlgeschlagen',
                description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                tone: 'danger',
              }),
          })
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}

// ─── Modal: neuen Lauf anlegen (mit Preview-Schritt) ───────────────────────

function defaultMonthSelectValues(): { year: number; month: number } {
  const now = new Date()
  const month = now.getUTCMonth() + 1
  const year = now.getUTCFullYear()
  return { year, month }
}

interface CreateModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onOpenRun: (runId: string) => void
}

function CreateBillingRunModal({ open, onOpenChange, onOpenRun }: CreateModalProps) {
  const { toast } = useToast()
  const preview = useBillingPreview()
  const create = useCreateBillingRun()
  const [year, setYear] = useState<number>(defaultMonthSelectValues().year)
  const [month, setMonth] = useState<number>(defaultMonthSelectValues().month)
  const [notes, setNotes] = useState('')
  const [previewData, setPreviewData] = useState<BillingPreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [duplicateRunId, setDuplicateRunId] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      const v = defaultMonthSelectValues()
      setYear(v.year)
      setMonth(v.month)
      setNotes('')
      setPreviewData(null)
      setError(null)
      setDuplicateRunId(null)
    }
  }, [open])

  function loadPreview() {
    setError(null)
    const input: RunBillingInput = { year, month }
    preview.mutate(input, {
      onSuccess: (data) => setPreviewData(data),
      onError: (err) => setError(err instanceof Error ? err.message : 'Vorschau fehlgeschlagen'),
    })
  }

  function handleCreate() {
    create.mutate(
      { year, month, notes: notes || null },
      {
        onSuccess: (r) => {
          toast({
            title: 'Abrechnungslauf erzeugt',
            description: `${r.itemsBilled} Posten, Summe ${fmtCurrency(r.totalAmountCents, r.currency)}`,
            tone: 'success',
          })
          onOpenChange(false)
        },
        onError: (err) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const data = (err as any)?.response?.data as { code?: string; details?: { existingRunId?: string } } | undefined
          if (data?.code === 'BILLING_RUN_DUPLICATE' && data.details?.existingRunId) {
            setDuplicateRunId(data.details.existingRunId)
          } else {
            setError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen')
          }
        },
      },
    )
  }

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: new Date(Date.UTC(2000, i, 1)).toLocaleDateString('de-DE', { month: 'long' }),
  }))

  const thisYear = new Date().getUTCFullYear()
  const yearOptions = [thisYear - 1, thisYear, thisYear + 1].map((y) => ({ value: String(y), label: String(y) }))

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Neuer Abrechnungslauf"
      description="Wähle die Periode, prüfe die Vorschau und erzeuge dann den Draft. Freigeben erfolgt in der Liste."
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Abbrechen
          </Button>
          <Button variant="ghost" size="sm" onClick={loadPreview} loading={preview.isPending}>
            Vorschau berechnen
          </Button>
          <Button variant="primary" size="sm" onClick={handleCreate} loading={create.isPending}>
            Lauf erzeugen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {duplicateRunId ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-small text-amber-800 flex items-center justify-between gap-2">
            <span>Für diesen Zeitraum existiert bereits ein aktiver Abrechnungslauf.</span>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => { onOpenChange(false); onOpenRun(duplicateRunId) }}
            >
              Öffnen
            </Button>
          </div>
        ) : null}
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Jahr"
            value={String(year)}
            onValueChange={(v) => setYear(Number(v))}
            options={yearOptions}
          />
          <Select
            label="Monat"
            value={String(month)}
            onValueChange={(v) => setMonth(Number(v))}
            options={monthOptions}
          />
        </div>

        <Textarea
          label="Notizen"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />

        {previewData ? <PreviewSummary data={previewData} /> : null}
      </div>
    </Modal>
  )
}

function PreviewSummary({ data }: { data: BillingPreview }) {
  return (
    <Card>
      <Card.Header>
        <Card.Title>Vorschau</Card.Title>
      </Card.Header>
      <Card.Body className="space-y-2">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Verträge" value={String(data.contractsBilled)} />
          <Stat label="Posten" value={String(data.itemsBilled)} />
          <Stat label="Summe" value={fmtCurrency(data.totalAmountCents, data.currency)} />
        </div>
        {data.items.length === 0 ? (
          <p className="text-caption text-gray-500">
            Keine fakturierbaren Positionen für diese Periode.
          </p>
        ) : (
          <div className="max-h-56 overflow-auto">
            <SimpleTable>
              <SimpleTable.Head>
                <SimpleTable.Tr>
                  <SimpleTable.Th>Vertrag</SimpleTable.Th>
                  <SimpleTable.Th>Position</SimpleTable.Th>
                  <SimpleTable.Th className="text-right">Menge</SimpleTable.Th>
                  <SimpleTable.Th className="text-right">EP</SimpleTable.Th>
                  <SimpleTable.Th className="text-right">Summe</SimpleTable.Th>
                  <SimpleTable.Th>Hinweis</SimpleTable.Th>
                </SimpleTable.Tr>
              </SimpleTable.Head>
              <SimpleTable.Body>
                {data.items.slice(0, 50).map((it) => (
                  <SimpleTable.Tr key={`${it.contractId}-${it.contractItemId ?? ''}`}>
                    <SimpleTable.Td>{it.contractNumber}</SimpleTable.Td>
                    <SimpleTable.Td>{it.description ?? '—'}</SimpleTable.Td>
                    <SimpleTable.Td className="text-right tabular-nums">
                      {(it.quantityX1000 / 1000).toLocaleString('de-DE')}
                    </SimpleTable.Td>
                    <SimpleTable.Td className="text-right tabular-nums">
                      {fmtCurrency(it.unitPriceCents, it.currency)}
                    </SimpleTable.Td>
                    <SimpleTable.Td className="text-right tabular-nums font-medium">
                      {fmtCurrency(it.totalCents, it.currency)}
                    </SimpleTable.Td>
                    <SimpleTable.Td>
                      {it.pricingNote ? <Badge variant="neutral" size="sm">{it.pricingNote}</Badge> : null}
                    </SimpleTable.Td>
                  </SimpleTable.Tr>
                ))}
              </SimpleTable.Body>
            </SimpleTable>
            {data.items.length > 50 ? (
              <p className="text-caption text-gray-400 pt-1">… {data.items.length - 50} weitere</p>
            ) : null}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-caption text-gray-500">{label}</p>
      <p className="text-h4 text-gray-900">{value}</p>
    </div>
  )
}
